/**
 * Dashboard Page
 * Main landing page with balance, quick actions, summaries, and insights
 *
 * ⚠️ GUARDRAIL: Home page layout is locked.
 * Only the "Ask FinList" block at the bottom can be modified.
 * Do NOT change layout, styling, or add AI widgets here.
 * All other AI features belong on feature pages (Shopping, Budget, Insights, etc.).
 * AI features must live in feature pages (Shopping, Budget, Insights, etc.),
 * not on the Home page.
 * ✅ Keep only the "Ask-FinList" bottom section and retain the old UI/UX.
 */

import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { LayoutDashboard, PlusCircle, RefreshCw } from 'lucide-react';
import toast from 'react-hot-toast';
import DashboardHeader from '@/components/DashboardHeader';
import BalanceCard from '@/components/BalanceCard';
import QuickActions from '@/components/QuickActions';
import CategorySummaryCards from '@/components/CategorySummaryCards';
import InsightsChart from '@/components/InsightsChart';
import SpendingAnalyticsPanel from '@/components/SpendingAnalyticsPanel';
import BottomNavigation from '@/components/BottomNavigation';
import AddTransaction from '@/components/AddTransaction';
import OnboardingTour from '@/components/OnboardingTour';
import JourneyProgressWidget from '@/components/JourneyProgressWidget';
import TodayWidget from '@/components/FinTasks/TodayWidget';
import { CATEGORY_TYPES } from '@/constants/categoryTaxonomy';
import useDashboardData from '@/hooks/useDashboardData';
import { useSyncStatus } from '@/hooks/useSyncStatus';
import { useAuth } from '@/contexts/AuthContext';
import { useOutsideClick } from '@/hooks/useOutsideClick';
import { completeJourneyStep, JOURNEY_STEPS } from '@/services/journeyService';
import FinListPageLayout from '@/components/layout/FinListPageLayout';
import FinListCard from '@/components/layout/FinListCard';
import Button from '@/components/ui/Button';
import { formatCurrency } from '@/utils/currencyUtils';
import { insightsAPI } from '@/services/api';
import { getLoginQuote } from '@/services/loginQuoteService';
// Note: Home page uses a fixed currency symbol as per the last good layout.

const activityFilters = [
  { id: 'all', label: 'All' },
  { id: 'shopping', label: 'Shopping' },
  { id: 'transactions', label: 'Transactions' },
  { id: 'refunds', label: 'Refunds' },
  { id: 'goals', label: 'Goals' }
];

const aiSuggestions = [
  'Where am I overspending?',
  'What should I focus on today?',
  'How close am I to my goals?'
];

const filterTypeMap = {
  shopping: 'SHOPPING',
  transactions: 'TRANSACTION',
  refunds: 'REFUND',
  goals: 'GOAL'
};

const priorityColors = {
  high: 'text-rose-600 bg-rose-50',
  medium: 'text-amber-600 bg-amber-50',
  low: 'text-emerald-600 bg-emerald-50'
};

function useGreeting(name = 'there') {
  const now = new Date();
  const hour = now.getHours();
  if (hour < 12) return `Good morning, ${name}`;
  if (hour < 17) return `Good afternoon, ${name}`;
  return `Good evening, ${name}`;
}

export default function DashboardPage() {
  const navigate = useNavigate();
  const { currentUser, loading: authLoading } = useAuth();
  const [showAddTransaction, setShowAddTransaction] = useState(false);
  const [transactionType, setTransactionType] = useState(CATEGORY_TYPES.EXPENSE);
  const [askFinListQuery, setAskFinListQuery] = useState('');
  const [aiResponse, setAiResponse] = useState(null);
  const [isAskingAI, setIsAskingAI] = useState(false);
  const [showAiModal, setShowAiModal] = useState(false);
  const addTransactionRef = useRef(null);

  useOutsideClick(addTransactionRef, () => {
    if (showAddTransaction) {
      setShowAddTransaction(false);
    }
  });

  // Redirect to sign-in if not authenticated
  useEffect(() => {
    if (!authLoading && !currentUser) {
      navigate('/signin');
    }
  }, [currentUser, authLoading, navigate]);

  // Track dashboard view in journey
  useEffect(() => {
    if (currentUser) {
      completeJourneyStep(JOURNEY_STEPS.VIEWED_DASHBOARD);
    }
  }, [currentUser]);

  // Load real data from Firebase
  const { 
    loading, 
    error, 
    balance, 
    income, 
    expenses, 
    categories, 
    insightsData, 
    historicalAnalytics,
    refresh 
  } = useDashboardData();

  // Refresh data when page becomes visible again (e.g., after navigating back from shopping list)
  // This ensures refunds and unbought updates are reflected even if real-time listener misses them
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        console.log('[OverviewPage] Page visible, refreshing dashboard data...');
        refresh();
      }
    };
    
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [refresh]);

  // Monitor sync status
  const syncStatus = useSyncStatus();

  function handleAddExpense() {
    setTransactionType(CATEGORY_TYPES.EXPENSE);
    setShowAddTransaction(true);
  }

  function handleAddIncome() {
    setTransactionType(CATEGORY_TYPES.INCOME);
    setShowAddTransaction(true);
  }

  async function handleTransactionSuccess(transaction) {
    console.log('Transaction added:', transaction);
    setShowAddTransaction(false);
    
    // Track journey progress
    if (transaction.type === CATEGORY_TYPES.INCOME) {
      await completeJourneyStep(JOURNEY_STEPS.ADDED_INCOME);
    }
    
    // Refresh dashboard data
    refresh();
  }

  // Show loading state while checking authentication
  if (authLoading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-slate-950 flex items-center justify-center transition-colors">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-teal-600 dark:border-teal-400 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600 dark:text-gray-400 font-medium">Loading...</p>
        </div>
      </div>
    );
  }

  // Don't render if not authenticated (will redirect)
  if (!currentUser) {
    return null;
  }

  // Show loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-slate-950 flex items-center justify-center transition-colors">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-teal-600 dark:border-teal-400 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600 dark:text-gray-400 font-medium">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  // Show error state
  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-slate-950 flex items-center justify-center p-4 transition-colors">
        <div className="max-w-md w-full bg-white dark:bg-slate-800 rounded-xl border-2 border-red-200 dark:border-red-800 p-6 text-center">
          <div className="w-16 h-16 bg-red-100 dark:bg-red-900/30 rounded-full flex items-center justify-center mx-auto mb-4">
            <span className="text-3xl">⚠️</span>
          </div>
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">Error Loading Data</h2>
          <p className="text-gray-600 dark:text-gray-400 mb-4">{error}</p>
          <button
            onClick={refresh}
            className="rounded-xl bg-violet-600 dark:bg-violet-500 px-6 py-2 text-sm font-medium text-white hover:bg-violet-700 dark:hover:bg-violet-600 focus:outline-none focus:ring-2 focus:ring-violet-400 focus:ring-offset-1 transition"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  const categoriesOverBudget = categories.filter(cat => cat.amount > cat.budget * 0.75).length;
  const headerDescription = balance > 0
    ? `You're pacing to save ${formatCurrency((balance * 0.3), 'GBP')} this month.`
    : 'Log your next purchase to unlock personalized insights.';

  // Handle Ask FinList interactions
  const handleAskFinList = async (query) => {
    if (!query.trim()) return;
    
    setIsAskingAI(true);
    setShowAiModal(true);
    setAiResponse(null);
    
    try {
      console.log('Asking AI:', query);
      const data = await insightsAPI.askQuestion(query);
      console.log('AI Response:', data);
      
      // apiClient.post returns response.data directly
      // Backend sends: { success: true, answer: "..." }
      if (data && data.answer) {
        setAiResponse(data.answer);
        toast.success('Got your answer!');
      } else {
        console.error('Unexpected response structure:', data);
        toast.error('Received unexpected response format');
        setAiResponse('FinList AI is temporarily unavailable. Please try again in a moment.');
      }
    } catch (error) {
      console.error('Error asking AI:', error);
      console.error('Error details:', error.response?.data || error.message);
      const offlineFallback =
        'FinList AI could not reach the server. Please check your connection or try again later.';
      setAiResponse(error.response?.data?.error || offlineFallback);
      toast.error(error.response?.data?.error || 'AI service unreachable right now.');
    } finally {
      setIsAskingAI(false);
    }
  };

  const handleSuggestionClick = (suggestion) => {
    setAskFinListQuery(suggestion);
    handleAskFinList(suggestion);
  };

  const handleAskSubmit = () => {
    handleAskFinList(askFinListQuery);
  };


  return (
    <>
      <FinListPageLayout>
        <OnboardingTour />

        <div className="space-y-6">
          <DashboardHeader syncStatus={syncStatus} />

          {/* Greeting & Login Quote */}
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
            className="bg-gradient-to-r from-emerald-50 to-teal-50 dark:from-teal-900/30 dark:to-emerald-900/30 border border-emerald-200 dark:border-teal-700/50 rounded-2xl p-4 shadow-sm"
          >
            <h2 className="text-lg font-semibold text-teal-700 dark:text-teal-400 mb-1">
              {useGreeting(currentUser?.displayName?.split(' ')[0] || 'there')} 👋
            </h2>
            <p className="text-sm text-gray-600 dark:text-gray-400 leading-relaxed">
              {getLoginQuote().emoji} {getLoginQuote().text}
            </p>
          </motion.div>

          {/* Section 1: Financial Summary */}
          <div className="grid gap-6 lg:grid-cols-1">
            <FinListCard variant="gradient" padding="p-0" bleed className="overflow-hidden">
              <BalanceCard
                balance={balance}
                income={income}
                expenses={expenses}
                currency="£"
              />
            </FinListCard>
          </div>

          {/* Section 2: Quick Actions & Transactions */}
          <section className="w-full mt-8">
            <div
              className="
                w-full 
                h-[300px] 
                max-h-[320px] 
                min-h-[260px]
                bg-white/10 
                dark:bg-slate-800/20
                backdrop-blur-lg 
                rounded-2xl 
                shadow-[0_8px_32px_rgba(0,0,0,0.15)]
                flex items-center justify-between
                px-6
                md:h-[280px]
                sm:flex-col sm:gap-6 sm:h-auto sm:py-6
              "
            >
              <QuickActions
                onAddExpense={handleAddExpense}
                onAddIncome={handleAddIncome}
              />
            </div>
          </section>
          
          <section className="w-full mt-8">
            <FinListCard padding="p-0" className="overflow-hidden">
              <InsightsChart
                data={insightsData}
                currency="£"
              />
            </FinListCard>
          </section>

          {/* Section 3: Budget & Category Analytics */}
          <div className="grid gap-6 mt-8">
            <FinListCard className="overflow-hidden">
              <CategorySummaryCards
                categories={categories}
                currency="£"
              />
            </FinListCard>
            <FinListCard className="overflow-hidden">
              <SpendingAnalyticsPanel analytics={historicalAnalytics} />
            </FinListCard>
          </div>

          {/* Section 4: Personal Productivity & Daily Routines */}
          <div className="grid gap-6 lg:grid-cols-2 mt-8">
            <FinListCard>
              <TodayWidget />
            </FinListCard>
            <FinListCard>
              <JourneyProgressWidget />
            </FinListCard>
          </div>

          {/* Quick Tips Section - Retained at the bottom */}
          <FinListCard>
            <h2 className="text-lg font-semibold text-slate-900 dark:text-white mb-4">
              Quick tips
            </h2>
            <div className="space-y-3">
              {balance > 0 && (
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="flex items-start gap-3 rounded-2xl border border-emerald-200/40 bg-emerald-50/60 p-3 text-emerald-800 dark:border-emerald-400/20 dark:bg-emerald-500/10 dark:text-emerald-200"
                >
                  <span className="text-lg" aria-hidden>💡</span>
                  <div>
                    <h3 className="text-sm font-semibold">Great savings this month</h3>
                    <p className="text-sm opacity-80">
                      You're on track to save £{(balance * 0.3).toFixed(0)} by month end.
                    </p>
                  </div>
                </motion.div>
              )}

              {categories.length > 0 && (
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="flex items-start gap-3 rounded-2xl border border-amber-200/40 bg-amber-50/70 p-3 text-amber-900 dark:border-amber-400/30 dark:bg-amber-500/10 dark:text-amber-200"
                >
                  <span className="text-lg" aria-hidden>📊</span>
                  <div>
                    <h3 className="text-sm font-semibold">Budget tracking</h3>
                    <p className="text-sm opacity-80">
                      {categoriesOverBudget > 0
                        ? `${categoriesOverBudget} categories are past 75% of their envelope.`
                        : 'All categories are within budget. Stellar pacing!'}
                    </p>
                  </div>
                </motion.div>
              )}

              {categories.length === 0 && balance === 0 && (
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="flex items-start gap-3 rounded-2xl border border-brand-200/50 bg-brand-50/80 p-3 text-brand-700 dark:border-brand-400/30 dark:bg-brand-500/10 dark:text-brand-200"
                >
                  <span className="text-lg" aria-hidden>👋</span>
                  <div>
                    <h3 className="text-sm font-semibold">Get started</h3>
                    <p className="text-sm opacity-80">
                      Add your first transaction to unlock tailored spending insights.
                    </p>
                  </div>
                </motion.div>
              )}
            </div>
          </FinListCard>

          {/* FINLIST: Ask FinList high-visibility section on Overview */}
          <section className="mt-6">
            <div
              id="finlist-ai-panel"
              className="rounded-xl bg-slate-900/80 border border-slate-800 px-4 py-3"
            >
              <h2 className="text-sm font-semibold text-slate-100 mb-1">
                Ask FinList
              </h2>

              <p className="text-xs font-medium tracking-wide text-slate-200">
                Powered by your household data &amp; FinList AI.
              </p>

              <div className="mt-3 flex flex-wrap gap-2">
                <button
                  type="button"
                  className="rounded-full border border-slate-500/80 px-3 py-1.5 text-xs font-medium text-slate-100 hover:bg-slate-800/80"
                  onClick={() => handleSuggestionClick('Where am I overspending?')}
                >
                  Where am I overspending?
                </button>

                <button
                  type="button"
                  className="rounded-full border border-slate-500/80 px-3 py-1.5 text-xs font-medium text-slate-100 hover:bg-slate-800/80"
                  onClick={() => handleSuggestionClick('What should I focus on today?')}
                >
                  What should I focus on today?
                </button>

                <button
                  type="button"
                  className="rounded-full border border-slate-500/80 px-3 py-1.5 text-xs font-medium text-slate-100 hover:bg-slate-800/80"
                  onClick={() => handleSuggestionClick('How close am I to my goals?')}
                >
                  How close am I to my goals?
                </button>

                <button
                  type="button"
                  className="rounded-full border border-slate-500/80 px-3 py-1.5 text-xs font-medium text-slate-100 hover:bg-slate-800/80"
                  onClick={() => handleSuggestionClick('Ask about your money this month...')}
                >
                  Ask about your money this month…
                </button>
              </div>

              <div className="mt-3 flex items-center gap-3">
                <input
                  type="text"
                  value={askFinListQuery}
                  onChange={(e) => setAskFinListQuery(e.target.value)}
                  onKeyPress={(e) => {
                    if (e.key === 'Enter') {
                      handleAskSubmit();
                    }
                  }}
                  className="flex-1 rounded-lg border border-slate-700/90 bg-slate-900/80 px-4 py-2.5 text-sm text-slate-100 placeholder:text-slate-300 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-violet-500"
                  placeholder="Ask FinList anything..."
                />
                <button
                  type="button"
                  onClick={handleAskSubmit}
                  disabled={!askFinListQuery.trim()}
                  className="rounded-lg bg-violet-600 px-4 py-2.5 text-sm font-semibold text-white hover:bg-violet-500 disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  Ask
                </button>
              </div>
            </div>
          </section>
        </div>
      </FinListPageLayout>

      <BottomNavigation />

      {showAddTransaction && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/70 backdrop-blur-sm p-4">
          <motion.div
            ref={addTransactionRef}
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.9 }}
            transition={{ duration: 0.3 }}
            className="w-full max-w-2xl max-h-[90vh] overflow-y-auto"
          >
            <AddTransaction
              initialType={transactionType}
              onSuccess={handleTransactionSuccess}
              onCancel={() => setShowAddTransaction(false)}
              source="dashboard_quick_add"
            />
          </motion.div>
        </div>
      )}

      {/* AI Response Modal */}
      {showAiModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/70 backdrop-blur-sm p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.9 }}
            transition={{ duration: 0.3 }}
            className="w-full max-w-2xl bg-white dark:bg-slate-800 rounded-2xl shadow-2xl overflow-hidden"
          >
            <div className="bg-gradient-to-r from-violet-600 to-purple-600 px-6 py-4 flex items-center justify-between">
              <h3 className="text-lg font-semibold text-white">FinList AI</h3>
              <button
                onClick={() => {
                  setShowAiModal(false);
                  setAskFinListQuery('');
                }}
                className="text-white/80 hover:text-white transition"
              >
                <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            
            <div className="p-6 max-h-[70vh] overflow-y-auto">
              {isAskingAI ? (
                <div className="flex flex-col items-center justify-center py-12">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-violet-600 mb-4"></div>
                  <p className="text-slate-600 dark:text-slate-300 text-sm">Analyzing your financial data...</p>
                </div>
              ) : aiResponse ? (
                <div className="space-y-4">
                  <div className="bg-violet-50 dark:bg-violet-900/30 border border-violet-200 dark:border-violet-700 rounded-lg p-4">
                    <p className="text-sm text-slate-600 dark:text-slate-300 mb-2">Your question:</p>
                    <p className="font-medium text-slate-800 dark:text-white">{askFinListQuery}</p>
                  </div>
                  
                  <div className="bg-slate-50 dark:bg-slate-700 border border-slate-200 dark:border-slate-600 rounded-lg p-4">
                    <p className="text-sm text-slate-600 dark:text-slate-300 mb-2">FinList AI says:</p>
                    <div className="prose prose-sm max-w-none text-slate-800 dark:text-slate-100">
                      {aiResponse}
                    </div>
                  </div>
                  
                  <div className="flex gap-2 pt-4">
                    <button
                      onClick={() => {
                        setAskFinListQuery('');
                        setAiResponse(null);
                      }}
                      className="flex-1 px-4 py-2 bg-violet-600 text-white rounded-lg hover:bg-violet-700 transition font-medium"
                    >
                      Ask Another Question
                    </button>
                    <button
                      onClick={() => {
                        setShowAiModal(false);
                        setAskFinListQuery('');
                      }}
                      className="px-4 py-2 bg-slate-200 dark:bg-slate-600 text-slate-700 dark:text-white rounded-lg hover:bg-slate-300 dark:hover:bg-slate-500 transition font-medium"
                    >
                      Close
                    </button>
                  </div>
                </div>
              ) : (
                <div className="space-y-4">
                  <p className="text-slate-600 dark:text-slate-300 text-sm">Ask FinList AI anything about your finances, spending habits, or budget goals.</p>
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={askFinListQuery}
                      onChange={(e) => setAskFinListQuery(e.target.value)}
                      onKeyPress={(e) => {
                        if (e.key === 'Enter' && askFinListQuery.trim()) {
                          handleAskSubmit();
                        }
                      }}
                      placeholder="Ask FinList anything..."
                      className="flex-1 rounded-xl border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-700 text-slate-900 dark:text-white placeholder-slate-400 dark:placeholder-slate-400 px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-violet-500"
                      autoFocus
                    />
                    <button
                      type="button"
                      onClick={handleAskSubmit}
                      disabled={!askFinListQuery.trim()}
                      className="rounded-xl bg-violet-600 px-6 py-3 text-sm font-medium text-white hover:bg-violet-700 focus:outline-none focus:ring-2 focus:ring-violet-400 focus:ring-offset-1 transition disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Ask
                    </button>
                  </div>
                  <div className="pt-2">
                    <p className="text-xs text-slate-500 dark:text-slate-400 mb-2">Try asking:</p>
                    <div className="flex flex-wrap gap-2">
                      {[
                        "What should I focus on today?",
                        "How can I save more?",
                        "Where am I spending the most?"
                      ].map((suggestion) => (
                        <button
                          key={suggestion}
                          onClick={() => setAskFinListQuery(suggestion)}
                          className="text-xs px-3 py-1.5 bg-violet-50 dark:bg-violet-900/40 text-violet-700 dark:text-violet-300 rounded-full hover:bg-violet-100 dark:hover:bg-violet-900/60 transition"
                        >
                          {suggestion}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </div>
          </motion.div>
        </div>
      )}
    </>
  );
}
